import { processApiFieldErrors } from '../../../../utils/processApiFieldErrors'
import { getErrorMessage } from '../../../../utils/utils'
import { GAME_BE_TO_FIELD_TITLE, GAME_BE_TO_FORM_FIELD } from './gameErrorMaps'

export interface ApplyGameApiErrorDeps {
  /** Set the inline per-field error map, keyed by form field name. */
  setFieldErrors: (errors: Record<string, string>) => void
  /** Summary toast (web: message.error). */
  notifyError: (message: string) => void
}

/**
 * Translate a thrown game API error into the form's inline field-error map --
 * the game counterpart of `applyFamilyApiError`. The form is useState
 * controlled rather than Formik, so this writes to a plain setter.
 *
 * Field errors render inline and do not also raise a toast, matching the
 * family and player forms. Anything the API rejects that has no mapped form
 * field falls back to the toast, so a validation failure is never silent.
 */
export const applyGameApiError = (
  err: unknown,
  { setFieldErrors, notifyError }: ApplyGameApiErrorDeps
): void => {
  const e = err as {
    invalid_fields?: Record<string, string | string[]>
    message?: string
  }

  const { formErrors, titles, hasFieldErrors, hasUnmapped } =
    processApiFieldErrors(e.invalid_fields, {
      fieldMap: GAME_BE_TO_FORM_FIELD,
      titleMap: GAME_BE_TO_FIELD_TITLE,
    })

  if (!hasFieldErrors || titles.length === 0) {
    // No field errors, or every key was unmapped -- clear inline errors and
    // surface the backend message.
    setFieldErrors({})
    notifyError(e.message ?? getErrorMessage(err))
    return
  }

  setFieldErrors(formErrors)

  // Some keys mapped and some did not: the unmapped ones have no inline slot,
  // so without this they would be dropped silently.
  if (hasUnmapped) {
    notifyError(e.message ?? getErrorMessage(err))
  }
}
